import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import withMountGuard from 'hocs/withMountGuard'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import Flag from 'LuxKit/Flag'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import LineSyncIcon from 'LuxKit/Icons/line/LineSyncIcon'
import config from 'constants/config'
import Sheet from 'components/utils/Sheet'
import NestableClickable from 'components/Common/NestableClickable'
import { pushWithRegion } from 'actions/NavigationActions'
import useSwitchRegion from 'hooks/useSwitchRegion'

const RootClickable = styled(NestableClickable)`
  &:focus {
    ${interactiveFocusStyle}
  }
`

const MemberText = styled.span`
  text-transform: none;
  font-weight: normal;
  white-space: nowrap;
`

const InlineFlag = styled(Flag)`
  margin-left: ${rem(8)};
  vertical-align: middle;
`

interface Props {
  onLinkClick: () => void
  loyaltyAccountData: App.LoyaltyAccount
}

function AccountMenuLoyaltyHasAccountDetailsTileNonMemberRegion({ onLinkClick, loyaltyAccountData }: Props) {
  const tier = loyaltyAccountData.tier
  const tierName = getLoyaltyTierDisplayName(tier)
  const loyaltyAccountRegion = loyaltyAccountData.region
  const dispatch = useAppDispatch()

  const loyaltyCurrency = useMemo(() => {
    return config.regions.find(r => r.code === loyaltyAccountRegion)?.currencyCode
  }, [loyaltyAccountRegion])

  const [switchRegionPath, handleSwitchRegion] = useSwitchRegion(loyaltyAccountRegion)

  const handleOnLinkClick = useCallback(() => {
    dispatch(pushWithRegion(`/${LOYALTY_LANDING_PAGE}`))
    onLinkClick()
  }, [onLinkClick, dispatch])

  return <ProductPaletteProvider palette={`tier-${tier}`}>
    <RootClickable
      onClick={handleOnLinkClick}
    >
      <Sheet
        background={`tier-${tier}-secondary`}
        padding="12 16"
        borderRadius="M"
      >
        <VerticalSpacer gap={20}>
          <VerticalSpacer gap={12}>
            <VerticalSpacer gap={4}>
              <Group direction="horizontal" horizontalAlign="space-between">
                <LoyaltyProgramNameLogo width={72} />
                <LoyaltyTierLabel kind="primary" tier={tier} format="uppercase">
                  {tierName}
                  <MemberText> · Member {loyaltyAccountData.accountNumber}</MemberText>
                  {loyaltyAccountRegion && (
                    <InlineFlag size="XS" countryCode={loyaltyAccountRegion} />
                  )}
                </LoyaltyTierLabel>
              </Group>
            </VerticalSpacer>
            <Heading variant="heading4">
              Looks like your account is linked to {loyaltyCurrency}
            </Heading>
            <BodyText variant="medium">
              To earn points and redeem your benefits you'll need to switch to the {loyaltyCurrency} site.
            </BodyText>
          </VerticalSpacer>
          <TextLink
            size="medium"
            endIcon={<LineSyncIcon size="XS" />}
            onClick={handleSwitchRegion}
            href={switchRegionPath}
            block
          >
            Switch to {loyaltyCurrency} site
          </TextLink>
        </VerticalSpacer>
      </Sheet>
    </RootClickable>
  </ProductPaletteProvider>
}

export default withMountGuard(() => {
  return useAppSelector(getIsLoyaltyEnabled)
})(AccountMenuLoyaltyHasAccountDetailsTileNonMemberRegion)
